import { useField, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { calculateDayRateTotal, calculateMonthlyRateTotal } from '../ConditionalFeeFields.helpers';
import { calculateBillingPeriod } from './calculateBillingPeriod';

export interface IMathOnFieldChangesProps {
  baseName: string;
  properties: { [key: string]: boolean };
}

const roundValue = (x: number) => {
  if (x) {
    return Math.round(x * 100) / 100;
  }
  return x;
};

export const MathOnFieldChanges: React.FC<IMathOnFieldChangesProps> = ({ baseName, properties }) => {
  const { isSubmitting } = useFormikContext();
  const [{ value: ratePerMonth }, { initialValue: initialRatePerMonth }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.RatePerMonth}`
  );
  const [{ value: numberOfMonths }, { initialValue: initialNumberOfMonths }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.NumberOfMonths}`
  );
  const [{ value: dayRate }, { initialValue: initialDayRate }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.DayRate}`
  );
  const [{ value: dateFrom }, { initialValue: initialDateFrom, touched: touchedDateFrom }] = useField<string>(
    `${baseName}.stringFeePropertyValues.${eFeePropertyNames.DateFrom}`
  );
  const [{ value: dateTo }, { initialValue: initialDateTo, touched: touchedDateTo }] = useField<string>(
    `${baseName}.stringFeePropertyValues.${eFeePropertyNames.DateTo}`
  );
  const [{ value: billingPeriod }, { touched: touchedBillingPeriod }] = useField<string>(
    `${baseName}.stringFeePropertyValues.${eFeePropertyNames.BillingPeriod}`
  );
  const [{ value: billingAmount }, { touched: touchedBillingAmount }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.BillingAmount}`
  );
  const [{ value: buyerPaidAtClosing }, , { setValue: setBuyerPaidAtClosing }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.BuyerPaidAtClosing}`
  );
  const [, , { setValue: setSellerPaidAtClosing }] = useField<number>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidAtClosing}`
  );

  // Rate Per Month and Number of Months.
  useEffect(() => {
    if (!properties[eFeePropertyNames.RatePerMonth] || !properties[eFeePropertyNames.NumberOfMonths]) {
      // Don't do this if both fields aren't allowed
      return;
    }
    if (ratePerMonth !== initialRatePerMonth || numberOfMonths !== initialNumberOfMonths) {
      const total = calculateMonthlyRateTotal(ratePerMonth, numberOfMonths);
      if (total !== undefined && roundValue(total) !== buyerPaidAtClosing && !Number.isNaN(total)) {
        setBuyerPaidAtClosing(roundValue(total));
      }
    }
  }, [
    ratePerMonth,
    numberOfMonths,
    initialNumberOfMonths,
    initialRatePerMonth,
    properties,
    setBuyerPaidAtClosing,
    buyerPaidAtClosing,
  ]);

  // Day Rate, Date From and Date To
  useEffect(() => {
    if (
      !properties[eFeePropertyNames.DayRate] ||
      !properties[eFeePropertyNames.DateFrom] ||
      !properties[eFeePropertyNames.DateTo]
    ) {
      // Don't do this is all 3 fields aren't allowed
      return;
    }
    if (dayRate !== initialDayRate || dateFrom !== initialDateFrom || dateTo !== initialDateTo) {
      const total = calculateDayRateTotal(dayRate, dateFrom, dateTo);
      if (total !== undefined && roundValue(total) !== buyerPaidAtClosing && !Number.isNaN(total)) {
        setBuyerPaidAtClosing(roundValue(total));
      }
    }
  }, [
    dayRate,
    initialDayRate,
    dateFrom,
    initialDateFrom,
    dateTo,
    initialDateTo,
    properties,
    setBuyerPaidAtClosing,
    buyerPaidAtClosing,
  ]);

  const needToDoBillingPeriod =
    properties[eFeePropertyNames.BillingPeriod] &&
    properties[eFeePropertyNames.BillingAmount] &&
    properties[eFeePropertyNames.DateFrom] &&
    properties[eFeePropertyNames.DateTo] &&
    (properties[eFeePropertyNames.SellerPaidAtClosing] || properties[eFeePropertyNames.BuyerPaidAtClosing]);
  // Billing Period, Billing Amount, Date From and Date To
  useEffect(() => {
    if (!needToDoBillingPeriod) {
      // We need all these things available in order to bother doing this math
      return;
    }
    if (!touchedBillingPeriod && !touchedBillingAmount && !touchedDateFrom && !touchedDateTo) {
      return;
    }

    if (isSubmitting) {
      return;
    }

    const total = calculateBillingPeriod(billingPeriod, billingAmount, dateFrom, dateTo);

    if (total === undefined || Number.isNaN(total)) {
      return;
    }
    if (properties[eFeePropertyNames.SellerPaidAtClosing]) {
      setSellerPaidAtClosing(roundValue(total));
    }
    if (properties[eFeePropertyNames.BuyerPaidAtClosing]) {
      setBuyerPaidAtClosing(roundValue(total));
    }
    // We ignore the deps issue because setSeller & setBuyer are not stable functions, and change on each iteration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingPeriod,
    touchedBillingPeriod,
    billingAmount,
    touchedBillingAmount,
    dateFrom,
    touchedDateFrom,
    dateTo,
    touchedDateTo,
    needToDoBillingPeriod,
    properties,
    isSubmitting,
  ]);

  return <></>;
};
